import { ChangeEvent, useEffect, useRef, useState } from 'react';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { useAuth } from '@/lib/auth';
import { useGenerateNewOtp, useLoginWithEmail } from '../api/login';
import './login.scss';
import PolygonIcon from '@/assets/rewise/polygon.svg';
import RefreshIcon from '@/assets/rewise/refresh.svg';
import RewsieLogo from '@/assets/full-logo.svg';
import BackIcon from '@/assets/rewise/back-icon.svg';
import FriendlyIcon from '@/assets/rewise/env-icon.svg';
import TimerIcon from '@/assets/rewise/timer-icon.svg';
import AccessIcon from '@/assets/rewise/access.svg';
import { showNotification } from '@/utils/notificationUtils';
import Utils from '@/utils/util';
import storage from '@/utils/storage';
import { useNavigate } from 'react-router-dom';

type LoginFormProps = {
  onSuccess: () => void;
};

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const { login, refetchUser } = useAuth();
  const getLoginDetails = useLoginWithEmail();
  const generateOtp = useGenerateNewOtp();
  const [isEmailAvailable, setIsEmailAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [email, setEmial] = useState('');
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const navigate = useNavigate();


  useEffect(() => {
    const token = storage.getToken();
    if(token){
      navigate('/funnel')
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0 && isActive) {
      setIsActive(false);
    }

    return () => clearInterval(timer);
  }, [isActive, timeLeft]);

  const startTimer = () => {

    setTimeLeft(30);
    setIsActive(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.id === 'email') {
      setEmial(event.target.value);
    }
  };

  const loginUserWithEmail = async () => {
    const resp: any = await getLoginDetails.mutateAsync({ email: email, isWeb: true });
    if (resp && resp.token) {
      storage.setToken(`Bearer ${resp.token}`);
      refetchUser();
    }
    if (resp && resp?.success) {
      setIsEmailAvailable(true);
      startTimer();
    }
  };

  const generateNewOtp = async () => {
    const resp: any = await generateOtp.mutateAsync({ email: email });
    if (resp && resp?.success) {
      showNotification({
        type: 'success',
        title: 'OTP sent at your email.',
      });
    }
  };


  const validateOTP = async () => {
    if (!otp) {
      showNotification({
        type: 'info',
        title: 'Please enter OTP.',
      });
    }else{
      await login({
        email,
        otp,
      });
      if (onSuccess) {
        onSuccess();
      } 
    }
  };

  const validate = () => {
    if (!email || email.trim().length < 1 || !Utils.isValidEmail(email)) {
      showNotification({
        type: 'info',
        title: 'Please enter valid email',
      });
      return false;
    }
    return true;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (validate()) {
        loginUserWithEmail();
      }
    }
  };

  return (
    <div className="home-page">
      <div className="login-screen">
        <div className="left-panel">
          <div className="polygon-icon-div">
            <img src={PolygonIcon} className="polygon-icon" />
          </div>
          <div className="rewise-info-panel">
            <div className="rewise-icon-div">
              <img src={RewsieLogo} className="rewise-icon" />
            </div>
            <div className="login-title">
            Sales & Team Management Solution
            </div>
            <div className="login-desc">
            Streamline your workflow by managing users, teams, sales executives, accounts, funnels, and orders all in one powerful platform.
            </div>
            <div className="rewise-details-div">
              <div className="rewise-details">
                <div className="img-container">
                  <img src={FriendlyIcon} />
                </div>
                <div className="details-container">
                  <div className="title">Sales Management</div>
                  <div className="desc">
                  </div>
                </div>
              </div>

              <div className="rewise-details">
                <div className="img-container">
                  <img src={AccessIcon} />
                </div>
                <div className="details-container">
                  <div className="title">Team Collaboration</div>
                  <div className="desc">
                  </div>
                </div>
              </div>

              <div className="rewise-details">
                <div className="img-container">
                  <img src={TimerIcon} />
                </div>
                <div className="details-container">
                  <div className="title">CRM (Customer Relationship Management)</div>
                  <div className="desc">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rewise-copy-right"> © 2024 Eduracle all rights reserved</div>
        </div>
        <div className={`right-panel`}>
          <div className={`top-panel ${isEmailAvailable ? 'is-email-available' : ''}  `}>
            {isEmailAvailable ? (
              <>
                <div className="back-btn-container" onClick={()=>{setIsEmailAvailable(false)}}>
                  <img src={BackIcon} />
                  <div className="text">Back</div>
                </div>
                <div className="login-container">
                  <div className="login-form-title">Verify email address</div>
                  <div className="login-form-desc">
                    Enter the 6-digit verification code we just sent you on your email{' '}
                    <span>{email}</span>
                  </div>
                  <div className="email-container">
                    <div className="otp-input-container">
                      <span className="positioner">
                        <OTPInput
                          value={otp}
                          onChange={setOtp}
                          autoFocus
                          OTPLength={6}
                          //otpType="number"
                          className="otp-input-root"
                          inputClassName="otp-input"
                          disabled={false}
                        />
                        <span className="seperator">
                          <span>-</span>
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="checkbox-container center">
                    <div className="looged-in-check">
                      Missed the code? No worries, {timeLeft !== 0 ? 'get again in ' : 'get again '} 
                      <span>
                        {timeLeft !== 0 ? `00: ${timeLeft}` : ''} {timeLeft === 0 && <img src={RefreshIcon} onClick={()=>{    generateNewOtp();startTimer()}}/>}
                      </span>
                    </div>
                  </div>
                  <div className="btn-container">
                    <button
                      className={`steam-button button-dark width-100`}
                      onClick={() => { validateOTP();
                      }}
                      disabled={isLoading}
                    >
                      <span>{isLoading ? 'Loading...' : 'Verify OTP'}</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="login-container">
                <div className="login-form-title">Login to your account</div>
                <div className="login-form-desc">
                  Log in below and start managing your funnels, accounts & users.
                </div>
                <div className="email-container">
                  <h1>Email address</h1>
                  <input
                    className={`custome-input`}
                    value={email}
                    placeholder={'Enter email'}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    id="email"
                    type="email"
                  />
                </div>
                <div className="checkbox-container">
                  {/* <label className="custom-checkbox-login">
                    <input type="checkbox" checked={isChecked} onChange={()=>{setIsChecked(!isChecked)}} />
                    <span className="checkmark-login"></span>
                  </label>
                  <div className="looged-in-check">Please keep me signed in</div> */}
                </div>
                <div className="btn-container">
                  <button
                    className={`steam-button button-dark width-100`}
                    onClick={async () => {
                      if (validate()) {
                        loginUserWithEmail();
                      }
                    }} 
                    disabled={getLoginDetails?.isLoading}
                  >
                    <span>{getLoginDetails?.isLoading ? 'Loading...' : 'Get started'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="privacy-policy-div"> Privacy policy | Terms and condition | Security</div>
        </div>
      </div>
    </div>
  );
};
